import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { environment } from '../../../environments/environment';
import { Observable, catchError, forkJoin, map, of, take } from "rxjs";
import { RequestsService } from "./utils/requests.service";
import { LoggerService } from "./utils/logger.service";
import { EventTypeCodes, INotificationData, canDisplayAlertEventType } from "../../shared/models/be/notification";

@Injectable({
    providedIn: 'any'
})
export class AlertsService {

    constructor(
        private _http: HttpClient,
        private requestsService: RequestsService,
        private loggerService: LoggerService
    ) {
    }

    public manageAlert$(notificationId: string) {
        return this._http.put<INotificationData[]>(
            `${environment.apiEndpoint}/notifications/${notificationId}/manage`, {}
        ).pipe(
            take(1),
            catchError(err => {
                this.loggerService.error("ERRORE manageAlert");
                throw err;
            })
        );
    }

    public getActiveAlertsList$(): Observable<INotificationData[]> {
        const date = new Date((new Date().getTime()) - 8 * 60 * 60 * 1000).toISOString();
        const buildingId = this.requestsService.getCurrentUser()?.building?.id
        const codeToSeverity: Record<EventTypeCodes, "low" | "medium" | "high"> = {
            "01": "high",
            "02": "medium",
            "03": "medium",
            "04": "medium",
            "05": "medium",
            "06": "low",
            "07": "low",
            "08": "low",
            "10": "low"
        };

        const scheduledAlerts$ = this._http.get<INotificationData[]>(
            `${environment.apiEndpoint}/notifications?status=active&buildingId=${buildingId}&from=${date}&sentToFcmClient=true`
        );
        // TEST must also survive refreshes outside the ordinary delivery/time window.
        const testAlerts$ = this.requestsService.getCurrentUser()?.role === 'superuser'
            ? this._http.get<INotificationData[]>(`${environment.apiEndpoint}/notifications?status=active&buildingId=${buildingId}`)
                .pipe(map((alerts) => alerts.filter((alert) => alert.eventType === '08')))
            : of([] as INotificationData[]);
        return forkJoin([scheduledAlerts$, testAlerts$]).pipe(
            take(1),
            map(([scheduled, tests]) => [...new Map([...scheduled, ...tests].map((alert) => [String(alert.id), alert])).values()]),
            map((alerts) => alerts
                .filter((alert) => canDisplayAlertEventType(alert.eventType, this.requestsService.getCurrentUser()?.role))
                .map((alert) => {
                    alert.severity = codeToSeverity[alert.eventType];
                    alert.timestamp = new Date(alert.timestamp + "");
                    return alert;
                })),
            catchError(err => {
                this.loggerService.error("ERRORE getActiveAlertsList");
                throw `error in source. Details:  + ${err}`;
            })
        );
    }

    public getAlert$(alertId: string): Observable<INotificationData> {
        return this._http.get<INotificationData>(
            `${environment.apiEndpoint}/notifications/${alertId}`
        ).pipe(
            take(1),
            map((alert) => {
                if (!canDisplayAlertEventType(alert.eventType, this.requestsService.getCurrentUser()?.role)) {
                    throw new Error('Notification is not available for the current role');
                }
                return alert;
            }),
            catchError(err => {
                this.loggerService.error("ERRORE getActiveAlertsList");
                throw `error in source. Details:  + ${err}`;
            })
        );
    }

    getAnalyticsAlerts$(filterDate: TimestampFilter = 'last24h'): Observable<INotificationData[]> {
        let sDate: string;
        let eDate = new Date(new Date().getTime()).toISOString();
        switch (filterDate) {
            case 'last6h':
                sDate = new Date(new Date().getTime() - (6 * 60 * 60 * 1000)).toISOString();
                break;
            case 'last24h':
                sDate = new Date(new Date().getTime() - (24 * 60 * 60 * 1000)).toISOString();
                break;
            case 'lastWeek':
                sDate = new Date(new Date().getTime() - (7 * 24 * 60 * 60 * 1000)).toISOString();
                break;
            case 'lastMonth':
                sDate = new Date(new Date().getFullYear(), new Date().getMonth() - 1, new Date().getDate()).toISOString();
                break;
        }
        const buildingId = this.requestsService.getCurrentUser()?.building?.id;
        const url = `${environment.apiEndpoint}/notifications?buildingId=${buildingId}&from=${sDate}&to=${eDate}`;
        const scheduledAlerts$ = this._http.get<INotificationData[]>(`${url}&sentToFcmClient=true`);
        const testAlerts$ = this.requestsService.getCurrentUser()?.role === 'superuser'
            ? this._http.get<INotificationData[]>(url).pipe(map((alerts) => alerts.filter((alert) => alert.eventType === '08')))
            : of([] as INotificationData[]);
        return forkJoin([scheduledAlerts$, testAlerts$]).pipe(
            take(1),
            map(([scheduled, tests]) => [...new Map([...scheduled, ...tests].map((alert) => [String(alert.id), alert])).values()]),
            map((alerts) => alerts.filter((alert) => canDisplayAlertEventType(alert.eventType, this.requestsService.getCurrentUser()?.role))),
            catchError(err => {
                this.loggerService.error("ERRORE getActiveAlertsList");
                throw `error in source. Details:  + ${err}`;
            })
        );
    }

    public deleteAlert$(alertId: string) {
        return this._http.delete(
            `${environment.apiEndpoint}/notifications/${alertId}`
        ).pipe(
            take(1),
            catchError(err => {
                //this.store.dispatch(createCustomerError())
                throw `error in source. Details:  + ${err}`
            })
        );
    }
}

export type TimestampFilter = 'last24h' | 'lastWeek' | 'lastMonth' | 'last6h';
